import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import * as yup from 'yup';

import { geoApi } from 'api/bestpark/geolocation';
import { logsApi } from 'api/bestpark/logs';
import { addPhoneCountryCode } from 'helpers/country-code';
import { setItem } from 'helpers/local-storage';
import { useTranslate } from 'hooks/translate';

import './identification.scss';
import { isValidLoginPhoneNumber } from 'helpers/phone-number-utils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as Sentry from '@sentry/browser';

interface LoginFormValues {
  phoneNumber: string;
}

interface LoginProps {
  next: Function;
  sendCode: Function;
}

export default function Login({ next, sendCode }: LoginProps): ReactElement {
  const { data } = useQuery(['userLang'], geoApi.getGeolocationData);
  const { t } = useTranslate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const loginSchema = yup.object().shape<LoginFormValues>({
    phoneNumber: yup
      .string()
      .test(
        'phone-number-test',
        t('reservation.registration.errors.phone'),
        function(value) {
          const { path, createError } = this;
          if (isValidLoginPhoneNumber(value)) {
            return true;
          }
          return createError({
            path,
            message: t('reservation.registration.errors.phone'),
          });
        },
      )
      .required(),
  });
  const { errors, handleSubmit, register } = useForm<LoginFormValues>({
    validationSchema: loginSchema,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: `${addPhoneCountryCode(data?.country, '') ?? ''}`,
    },
  });
  const [hasErrors, setErrors] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const getRecaptchaToken = useCallback(async () => {
    try {
      if (!executeRecaptcha) {
        return;
      }

      return await executeRecaptcha('customerLogin');
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setLevel(Sentry.Severity.Error);
        Sentry.captureMessage('reCAPTCHA v3 error');
        Sentry.captureException(error);
      });
    }
  }, [executeRecaptcha]);

  const onSubmit = useCallback(
    async ({ phoneNumber }: LoginFormValues): Promise<void> => {
      try {
        setLoading(true);
        const reCaptchaToken = await getRecaptchaToken();
        if (!reCaptchaToken) throw new Error('Error getting reCAPTCHA token.');
        const loginPhoneNumber = await sendCode(phoneNumber, reCaptchaToken);
        setItem('BC.REGISTRATION_PHONE_NUMBER', loginPhoneNumber);
        logsApi.postLog('Customer', 'Authentication Code Sent', {
          phoneNumber,
        });
        next();
      } catch (error) {
        logsApi.postLog('Customer', 'Failed Sending Verification Code', {
          phoneNumber,
        });
      }

      setLoading(false);
    },
    [next, sendCode, getRecaptchaToken],
  );

  useEffect(() => {
    if (errors.phoneNumber) {
      setErrors(true);
    } else {
      setErrors(false);
    }
  }, [errors.phoneNumber, setErrors]);

  return (
    <div className='booking-content booking-login'>
      <h1>{t('reservation.registration.phoneLabel')}</h1>
      <div className='booking-forms'>
        <label>
          {t('reservation.registration.phone')}{' '}
          <span>{t('reservation.registration.phoneInfo')}</span>
        </label>
        <form onSubmit={handleSubmit(onSubmit)}>
          <input
            autoFocus
            className={errors?.phoneNumber?.message && 'input-error'}
            disabled={isLoading}
            id='phoneNumber'
            name='phoneNumber'
            placeholder='e.g. +358401234567'
            ref={register()}
            type='text'
          />
          {errors?.phoneNumber && (
            <div className='form-error'>{errors.phoneNumber.message ?? ''}</div>
          )}
          <button disabled={isLoading || hasErrors} type='submit'>
            {isLoading ? t('loading') : t('reservation.registration.next')}
          </button>
        </form>
      </div>
    </div>
  );
}
